export type ListMeta = {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
};

type ListMetaFallback = {
  page: number;
  pageSize: number;
  rowCount: number;
};

function nonNegativeInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return Math.floor(value);
}

function positiveInteger(value: unknown): number | null {
  const parsed = nonNegativeInteger(value);
  return parsed && parsed > 0 ? parsed : null;
}

/**
 * Normalizes the API envelope metadata without making it a hard requirement.
 * Older endpoints can keep returning an empty meta object while newer,
 * paginated endpoints expose the full result count.
 */
export function normalizeListMeta(
  meta: Record<string, unknown> | null | undefined,
  fallback: ListMetaFallback,
): ListMeta {
  const page = positiveInteger(meta?.page) ?? Math.max(1, fallback.page);
  const pageSize =
    positiveInteger(meta?.page_size) ?? Math.max(1, fallback.pageSize);
  const total = nonNegativeInteger(meta?.total) ?? fallback.rowCount;
  const explicitPages = nonNegativeInteger(meta?.pages);
  const pages = explicitPages ?? (total === 0 ? 0 : Math.ceil(total / pageSize));

  return { page, pageSize, total, pages };
}
